import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import { CompanyFile } from 'src/company/types';
import { companyFilesQueryKey } from './useCompanyFiles';

type PresignedUploadResponse = {
  url: string;
  location: string;
  uploadToken: string;
};

type CreateCompanyFileRequest = {
  fileName: string;
  fileSize?: number;
  fileType?: string;
  isFolder: boolean;
  parentId?: number | null;
  location?: string;
  uploadToken?: string;
};

const requestPresignedUpload = async (file: File): Promise<PresignedUploadResponse> => {
  const { data } = await api.post<PresignedUploadResponse>('company-files/pre-signed-upload-url', {
    fileName: file.name,
  });
  return data;
};

const uploadToS3 = async (file: File, url: string): Promise<void> => {
  const uploadResponse = await fetch(url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });
  if (!uploadResponse.ok) {
    throw new Error(`Failed to upload file. Status: ${uploadResponse.status}`);
  }
};

export const useCompanyFileActions = () => {
  const queryClient = useQueryClient();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: companyFilesQueryKey });

  const {
    mutateAsync: deleteFileMutation,
    error: deleteError,
    reset: resetDeleteError,
    isPending: isDeleting,
  } = useMutation<unknown, Error, string>({
    mutationFn: (fileId: string) => api.delete(`company-files/${fileId}`),
    onSuccess: invalidate,
  });

  const deleteFile = async (fileId: string) => {
    await deleteFileMutation(fileId);
  };

  const moveFile = async (file: UploadedFile, parentId: string | null | undefined) => {
    await api.put(`company-files/${file.id}`, {
      parentId: parentId ?? null,
      isResolved: file.isResolved,
      isFiled: true,
      fileName: null,
    });
    invalidate();
  };

  const createFolder = async (name: string, parentId?: string) => {
    const body: CreateCompanyFileRequest = {
      fileName: name,
      isFolder: true,
      parentId: parentId != null ? Number(parentId) : null,
    };
    await api.post('company-files', body);
    invalidate();
  };

  const renameFolder = async (file: UploadedFile, newName: string) => {
    await api.put(`company-files/${file.id}`, {
      isResolved: file.isResolved,
      isFiled: false,
      parentId: file.parentId ?? null,
      fileName: newName,
    });
    invalidate();
  };

  const uploadFile = async (input: {
    file: File;
    parentId?: string;
  }): Promise<{ data: CompanyFile }> => {
    const presigned = await requestPresignedUpload(input.file);
    await uploadToS3(input.file, presigned.url);
    const body: CreateCompanyFileRequest = {
      fileName: input.file.name,
      fileSize: input.file.size,
      fileType: input.file.type,
      isFolder: false,
      parentId: input.parentId != null ? Number(input.parentId) : null,
      location: presigned.location,
      uploadToken: presigned.uploadToken,
    };
    const response = await api.post<CompanyFile>('company-files', body);
    return response;
  };

  const { mutateAsync: uploadFilesMutation } = useMutation<
    { data: CompanyFile }[],
    Error,
    { file: File; parentId?: string }[]
  >({
    mutationFn: async inputs => {
      const results: { data: CompanyFile }[] = [];
      for (const input of inputs) {
        results.push(await uploadFile(input));
      }
      return results;
    },
    onSuccess: invalidate,
  });

  const uploadFiles = async (inputs: { file: File; parentId?: string }[]) => {
    return uploadFilesMutation(inputs);
  };

  return {
    deleteFile,
    deleteError,
    resetDeleteError,
    isDeleting,
    moveFile,
    createFolder,
    renameFolder,
    uploadFiles,
  };
};
